import { streamLainAnimationController } from '@blocksuite/affine/shared/utils';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
} from 'react';

import type { SceneStory } from './scene-story';
import * as styles from './tutorial-scene.css';

/**
 * Plays a story on the scene's parts while the stage is on screen, all tracks
 * from one start time so they stay in step. The markup is the still frame:
 * without motion (reduced motion, motion setting off, no visibility signal)
 * the stage keeps showing it.
 */
function useSceneLoop(
  stageRef: RefObject<HTMLDivElement | null>,
  story: SceneStory
) {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof IntersectionObserver === 'undefined') return;
    const parts = story.tracks.map(track => {
      const element = stage.querySelector(`[data-scene-part="${track.part}"]`);
      if (!element) {
        throw new Error(`Scene part "${track.part}" is missing`);
      }
      return { element, track };
    });
    let playing = false;
    const stop = () => {
      playing = false;
      for (const { element } of parts) {
        streamLainAnimationController.cancel(element);
      }
    };
    const play = () => {
      if (playing || !streamLainAnimationController.isEnabled(stage)) return;
      playing = true;
      const startTime = Number(stage.ownerDocument.timeline.currentTime ?? 0);
      for (const { element, track } of parts) {
        streamLainAnimationController.playLoop(
          element,
          track.keyframes,
          track.durationMs ?? story.loopMs,
          startTime
        );
      }
    };
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) play();
      else stop();
    });
    observer.observe(stage);
    return () => {
      observer.disconnect();
      stop();
    };
  }, [stageRef, story]);
}

/**
 * A replica of a piece of the app at its design size, scaled to the width it
 * gets (`--u` is one design pixel). Decorative: the surrounding copy carries
 * the meaning, the stage is hidden from assistive technology.
 */
export function TutorialScene({
  story,
  width,
  height,
  className,
  testId,
  children,
}: {
  story: SceneStory;
  width: number;
  height: number;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  useSceneLoop(stageRef, story);
  return (
    <div
      aria-hidden="true"
      className={clsx(styles.stage, className)}
      data-testid={testId}
      ref={stageRef}
      style={assignInlineVars({
        [styles.designWidth]: String(width),
        [styles.designHeight]: String(height),
      })}
    >
      <div className={styles.frame}>{children}</div>
    </div>
  );
}

/** Places a scene element at its design box. */
export function sceneBox(box: {
  x: number;
  y: number;
  w?: number;
  h?: number;
}): CSSProperties {
  return assignInlineVars({
    [styles.boxX]: String(box.x),
    [styles.boxY]: String(box.y),
    [styles.boxW]: String(box.w ?? 0),
    [styles.boxH]: String(box.h ?? 0),
  });
}

/**
 * The viewer's own pointer, resting at x/y in the still frame. The layer
 * spans the stage so the story's translate percentages are stage
 * percentages; `text` stacks the I-beam the editor shows over text.
 */
export function ScenePointer({
  x,
  y,
  text = false,
  showText = false,
  size,
}: {
  x: number;
  y: number;
  text?: boolean;
  showText?: boolean;
  size: { width: number; height: number };
}) {
  return (
    <div
      className={styles.pointerLayer}
      data-scene-part="pointer"
      style={{
        transform: `translate(${(x / size.width) * 100}%, ${(y / size.height) * 100}%)`,
      }}
    >
      <span className={styles.clickRing} data-scene-part="ring" />
      <span className={styles.pointer} data-scene-part="press">
        <svg
          className={styles.pointerArrow}
          data-scene-part={text ? 'arrow' : undefined}
          style={showText ? { opacity: 0 } : undefined}
          viewBox="0 0 20 20"
        >
          <path d="M2 1.5v14.8l3.9-3.7 2.5 5.8 2.7-1.2-2.5-5.6h5.4z" />
        </svg>
        {text ? (
          <svg
            className={styles.pointerText}
            data-scene-part="ibeam"
            style={showText ? { opacity: 1 } : undefined}
            viewBox="0 0 24 24"
          >
            <path
              className={styles.pointerTextHalo}
              d="M9 4.5c1.4 0 2.4.4 3 1.1.6-.7 1.6-1.1 3-1.1M9 19.5c1.4 0 2.4-.4 3-1.1.6.7 1.6 1.1 3 1.1M12 5.6v12.8"
            />
            <path d="M9 4.5c1.4 0 2.4.4 3 1.1.6-.7 1.6-1.1 3-1.1M9 19.5c1.4 0 2.4-.4 3-1.1.6.7 1.6 1.1 3 1.1M12 5.6v12.8" />
          </svg>
        ) : null}
      </span>
    </div>
  );
}
